import React, { useEffect, useMemo, useRef, useState } from "react";
import { Button, DropDownButton, DropDownButtonItemClickEvent } from "@progress/kendo-react-buttons";
import { SvgIcon } from "@progress/kendo-react-common";
import { Checkbox, Input } from "@progress/kendo-react-inputs";
import { useLocalization } from "@progress/kendo-react-intl";
import { Popup } from "@progress/kendo-react-popup";
import { Tooltip } from "@progress/kendo-react-tooltip";
import {
  filterIcon,
  downloadIcon,
  arrowsSwapIcon,
  minusIcon,
  xIcon,
} from "@progress/kendo-svg-icons";
import { enMessages } from "../../../../../messages/en-US";
import { getLocalizedStreamOptions } from "../Overview/Dialogs/AnomalyDetector/AnomalyDetectorForm.utils";
import {
  FilterOption,
  FilterSection,
  FilterSectionKey,
  InsightTile,
  NO_CRITICALITY_FILTER_VALUE,
  getCriticalityIconDetails,
  getStreamIconDetails,
} from "./insightsUtils";
import type { InsightSortField, ProcessedInsightRow } from "./types";

interface InsightsAnomaliesSidebarProps {
  items: ProcessedInsightRow[];
  allItemsCount: number;
  searchValue: string;
  sortField: InsightSortField;
  selectedStreamValues: string[];
  selectedCriticalityValues: string[];
  selectedTypeValues: string[];
  selectedSourceValues: string[];
  selectedAnomalyId: string | null;
  hidePointName?: boolean;
  onSearchChange: (value: string) => void;
  onSortFieldChange: (field: InsightSortField) => void;
  onSelectedStreamsChange: (values: string[]) => void;
  onSelectedCriticalitiesChange: (values: string[]) => void;
  onSelectedTypesChange: (values: string[]) => void;
  onSelectedSourcesChange: (values: string[]) => void;
  onSelectAnomaly: (id: string) => void;
  onExport: () => void;
}


export default function InsightsAnomaliesSidebar({
  items,
  allItemsCount,
  searchValue,
  sortField,
  selectedStreamValues,
  selectedCriticalityValues,
  selectedTypeValues,
  selectedSourceValues,
  selectedAnomalyId,
  hidePointName = false,
  onSearchChange,
  onSortFieldChange,
  onSelectedStreamsChange,
  onSelectedCriticalitiesChange,
  onSelectedTypesChange,
  onSelectedSourcesChange,
  onSelectAnomaly,
  onExport,
}: InsightsAnomaliesSidebarProps): React.ReactElement {
  const localizationService = useLocalization();
  const [isFilterPopupOpen, setIsFilterPopupOpen] = useState(false);
  const [expandedSections, setExpandedSections] = useState<Record<FilterSectionKey, boolean>>({
    streams: true,
    criticality: true,
    type: true,
    source: true,
  });
  const filterButtonAnchorRef = useRef<HTMLDivElement | null>(null);
  const filterPopupContentRef = useRef<HTMLDivElement | null>(null);
  const localizedStreamOptions = useMemo(
    () =>
      getLocalizedStreamOptions(localizationService).map((option) => {
        const streamIcon = getStreamIconDetails(option.value, option.text, localizationService);

        return {
          ...option,
          iconContent: <span className="text-[8px] font-semibold">{streamIcon.badge}</span>,
        };
      }),
    [localizationService]
  );
  const criticalityOptions = useMemo<FilterOption[]>(
    () => [
      ...["Critical", "High", "Medium", "Low"].map((value) => {
        const details = getCriticalityIconDetails(value);

        return {
          value,
          text: localizationService.toLanguageString(`custom.${value.toLowerCase()}`, value),
          iconContent: details.icon ? <SvgIcon icon={details.icon} size="small" style={{ color: details.color }} /> : null,
        };
      }),
      {
        value: NO_CRITICALITY_FILTER_VALUE,
        text: localizationService.toLanguageString("custom.noCriticality", enMessages.custom.noCriticality),
        iconContent: <SvgIcon icon={minusIcon} size="small" className="text-gray-400" />,
      },
    ],
    [localizationService]
  );
  const typeOptions = useMemo<FilterOption[]>(
    () => [
      { value: "anomaly", text: localizationService.toLanguageString("custom.anomaly", "Anomaly") },
      { value: "anchor", text: localizationService.toLanguageString("custom.anchor", "Anchor") },
    ],
    [localizationService]
  );
  const sourceOptions = useMemo<FilterOption[]>(
    () => [
      { value: "ml", text: localizationService.toLanguageString("custom.ml", "ML") },
      { value: "user", text: localizationService.toLanguageString("custom.user", "User") },
    ],
    [localizationService]
  );
  const streamLabelByValue = useMemo(
    () => new Map(localizedStreamOptions.map((option) => [option.value, option.text])),
    [localizedStreamOptions]
  );
  const sortItems = [
    { text: localizationService.toLanguageString("custom.period", enMessages.custom.period), field: "period" as const },
    {
      text: localizationService.toLanguageString("custom.criticality", enMessages.custom.criticality),
      field: "criticality" as const,
    },
  ];

  const handleSortItemClick = (event: DropDownButtonItemClickEvent) => {
    const nextSortField = sortItems.find((item) => item.text === event.item.text)?.field;
    if (nextSortField) {
      onSortFieldChange(nextSortField);
    }
  };
  const currentSortLabel =
    sortField === "period"
      ? localizationService.toLanguageString("custom.period", enMessages.custom.period)
      : localizationService.toLanguageString("custom.criticality", enMessages.custom.criticality);
  const allStreamValues = localizedStreamOptions.map((option) => option.value);
  const allCriticalityValues = criticalityOptions.map((option) => option.value);
  const allTypeValues = typeOptions.map((option) => option.value);
  const allSourceValues = sourceOptions.map((option) => option.value);
  const totalFilterLeafCount =
    allStreamValues.length + allCriticalityValues.length + allTypeValues.length + allSourceValues.length;
  const selectedFilterLeafCount =
    selectedStreamValues.length +
    selectedCriticalityValues.length +
    selectedTypeValues.length +
    selectedSourceValues.length;
  const filterButtonTitle = localizationService.toLanguageString("custom.filter", "Filter");
  const areAllFiltersSelected = selectedFilterLeafCount === totalFilterLeafCount;
  const toggleSectionExpanded = (section: FilterSectionKey) => {
    setExpandedSections((current) => ({
      ...current,
      [section]: !current[section],
    }));
  };
  const toggleValue = (selectedValues: string[], onChange: (values: string[]) => void, value: string) => {
    onChange(selectedValues.includes(value) ? selectedValues.filter((item) => item !== value) : [...selectedValues, value]);
  };
  const handleToggleAllFilters = () => {
    if (areAllFiltersSelected) {
      onSelectedStreamsChange([]);
      onSelectedCriticalitiesChange([]);
      onSelectedTypesChange([]);
      onSelectedSourcesChange([]);
      return;
    }

    onSelectedStreamsChange(allStreamValues);
    onSelectedCriticalitiesChange(allCriticalityValues);
    onSelectedTypesChange(allTypeValues);
    onSelectedSourcesChange(allSourceValues);
  };
  useEffect(() => {
    if (!isFilterPopupOpen) {
      return undefined;
    }

    const handlePointerDown = (event: MouseEvent) => {
      const target = event.target as Node;
      if (filterButtonAnchorRef.current?.contains(target) || filterPopupContentRef.current?.contains(target)) {
        return;
      }

      setIsFilterPopupOpen(false);
    };

    document.addEventListener("mousedown", handlePointerDown);
    return () => {
      document.removeEventListener("mousedown", handlePointerDown);
    };
  }, [isFilterPopupOpen]);

  return (
    <div className="flex h-full min-h-0 flex-col overflow-hidden rounded-xl border border-gray-200 bg-gradient-to-b from-gray-50 to-white">
      <div className="border-b border-gray-200 px-2 py-2">
        <div className="flex items-center justify-between gap-3">
          <div className="flex items-center gap-2">
            <Tooltip anchorElement="target" position="bottom" parentTitle={true}>
              <DropDownButton
                svgIcon={arrowsSwapIcon}
                onItemClick={handleSortItemClick}
                items={sortItems}
                title={`${localizationService.toLanguageString("custom.sort", enMessages.custom.sort)}: ${currentSortLabel}`}
                className="!min-w-0"
              />
            </Tooltip>
            <div ref={filterButtonAnchorRef}>
              <Tooltip anchorElement="target" position="bottom" parentTitle={true}>
                <Button
                  themeColor="base"
                  svgIcon={filterIcon}
                  title={filterButtonTitle}
                  onClick={() => setIsFilterPopupOpen((current) => !current)}
                />
              </Tooltip>
              <Popup anchor={filterButtonAnchorRef.current} show={isFilterPopupOpen} popupClass={"my-multiselect-popup"}>
                <div ref={filterPopupContentRef} className="w-[300px] rounded-md border border-gray-200 bg-white p-3 shadow-lg">
                  <div className="max-h-[320px] overflow-auto">
                    <div className="border-b border-gray-100 pb-2">
                      <Button
                        fillMode="flat"
                        themeColor="base"
                        onClick={handleToggleAllFilters}
                        className="!flex !w-full !items-center !justify-start !rounded-md !px-2 !py-1.5 !text-left hover:!bg-gray-50"
                      >
                        <span className="flex items-center gap-2 text-sm font-medium text-gray-800">
                          <Checkbox checked={areAllFiltersSelected} onChange={() => {}} />
                          <span>{localizationService.toLanguageString("custom.all", "All")}</span>
                        </span>
                      </Button>
                    </div>
                    <FilterSection
                      title={localizationService.toLanguageString("custom.streams", enMessages.custom.streams)}
                      expanded={expandedSections.streams}
                      options={localizedStreamOptions}
                      selectedValues={selectedStreamValues}
                      onToggleExpanded={() => toggleSectionExpanded("streams")}
                      onToggleAll={() => onSelectedStreamsChange(selectedStreamValues.length === allStreamValues.length ? [] : allStreamValues)}
                      onToggleValue={(value) => toggleValue(selectedStreamValues, onSelectedStreamsChange, value)}
                    />
                    <FilterSection
                      title={localizationService.toLanguageString("custom.criticality", enMessages.custom.criticality)}
                      expanded={expandedSections.criticality}
                      options={criticalityOptions}
                      selectedValues={selectedCriticalityValues}
                      onToggleExpanded={() => toggleSectionExpanded("criticality")}
                      onToggleAll={() =>
                        onSelectedCriticalitiesChange(
                          selectedCriticalityValues.length === allCriticalityValues.length ? [] : allCriticalityValues
                        )
                      }
                      onToggleValue={(value) => toggleValue(selectedCriticalityValues, onSelectedCriticalitiesChange, value)}
                    />
                    <FilterSection
                      title={localizationService.toLanguageString("custom.type", "Type")}
                      expanded={expandedSections.type}
                      options={typeOptions}
                      selectedValues={selectedTypeValues}
                      onToggleExpanded={() => toggleSectionExpanded("type")}
                      onToggleAll={() => onSelectedTypesChange(selectedTypeValues.length === allTypeValues.length ? [] : allTypeValues)}
                      onToggleValue={(value) => toggleValue(selectedTypeValues, onSelectedTypesChange, value)}
                    />
                    <FilterSection
                      title={localizationService.toLanguageString("custom.source", "Source")}
                      expanded={expandedSections.source}
                      options={sourceOptions}
                      selectedValues={selectedSourceValues}
                      onToggleExpanded={() => toggleSectionExpanded("source")}
                      onToggleAll={() =>
                        onSelectedSourcesChange(
                          selectedSourceValues.length === allSourceValues.length ? [] : allSourceValues
                        )
                      }
                      onToggleValue={(value) => toggleValue(selectedSourceValues, onSelectedSourcesChange, value)}
                    />
                  </div>
                </div>
              </Popup>
            </div>
          </div>
          <div className="flex items-center gap-2">
            <Tooltip anchorElement="target" position="bottom" parentTitle={true}>
              <Button
                themeColor="base"
                onClick={onExport}
                svgIcon={downloadIcon}
                disabled={allItemsCount === 0}
                title={localizationService.toLanguageString("custom.exportToExcel", enMessages.custom.exportToExcel)}
              />
            </Tooltip>
          </div>
        </div>
        <div className="relative mt-3">
          <Input
            value={searchValue}
            onChange={(event) => onSearchChange(event.value ?? "")}
            placeholder={localizationService.toLanguageString("custom.searchInsights", enMessages.custom.searchInsights)}
          />
          {searchValue && (
            <Tooltip anchorElement="target" position="bottom" parentTitle={true}>
              <Button
                type="button"
                fillMode="flat"
                themeColor="base"
                size="small"
                svgIcon={xIcon}
                onClick={() => onSearchChange("")}
                className="!absolute right-2 top-1/2 -translate-y-1/2 !min-w-0 !p-1"
                title={localizationService.toLanguageString("custom.clearSearch", enMessages.custom.clearSearch)}
              />
            </Tooltip>
          )}
        </div>
      </div>
      <div className="flex-1 overflow-auto p-2">
        {allItemsCount === 0 ? (
          <div className="flex min-h-[220px] items-center justify-center text-center text-sm text-gray-500">
            {localizationService.toLanguageString(
              "custom.noAnomaliesFoundForSelectedPeriod",
              enMessages.custom.noAnomaliesFoundForSelectedPeriod
            )}
          </div>
        ) : items.length === 0 ? (
          <div className="flex min-h-[220px] items-center justify-center text-center text-sm text-gray-500">
            {!searchValue ||
            selectedStreamValues.length === 0 ||
            selectedTypeValues.length === 0 ||
            selectedSourceValues.length === 0
              ? localizationService.toLanguageString("custom.noDataFound", enMessages.custom.noDataFound)
              : localizationService.toLanguageString("custom.noAnomaliesMatchSearch", enMessages.custom.noAnomaliesMatchSearch)}
          </div>
        ) : (
          <div className="flex flex-col gap-1">
            {items.map((item) => (
              <InsightTile
                key={item.id}
                item={item}
                selected={item.id === selectedAnomalyId}
                hidePointName={hidePointName}
                localizationService={localizationService}
                streamLabelByValue={streamLabelByValue}
                onClick={() => onSelectAnomaly(item.id)}
              />
            ))}
          </div>
        )}
      </div>
    </div>
  );
}
